/**
 * announcer — multi-voice MP3 announcer for match events AND menu navigation.
 *
 * Assets (generated out-of-band, may be missing or incomplete — every failure
 * here is swallowed):
 *   client/public/audio/announcer/manifest.json
 *     → { generatedAt, lines: string[], packs: [{id, label}] }
 *   client/public/audio/announcer/<packId>/<lineId>.mp3
 *
 * Split in two layers so the interesting parts are unit-testable in the node
 * vitest environment:
 *   - AnnouncerScheduler — pure "what may speak when" logic: one clip at a
 *     time, priority interrupt/queue/drop, per-line cooldowns, ~250 ms global
 *     min-gap. No DOM, injectable clock.
 *   - AnnouncerSettings — enabled/volume/pack persistence, storage-injectable
 *     (uiSfx.ts pattern, same "neon-arena.*" key style).
 *   - Announcer — the browser shell: lazy manifest fetch, HTMLAudioElement
 *     pool per pack, autoplay-policy-safe playback (rejections swallowed; a
 *     rejected play frees the line's cooldown so a post-gesture retry works).
 */

export const ANNOUNCER_LINES = [
  "match_found", "countdown_3", "countdown_2", "countdown_1", "go",
  "round_start", "time_60", "time_10", "round_end", "winner", "victory",
  "first_blood", "kill", "kill_streak_3", "kill_streak_5", "death",
  "lead_taken", "powerup_overdrive", "powerup_spread", "powerup_armour",
  "pickup_rocket", "pickup_ricochet", "pickup_shockwave", "rocket_warning",
  "title_intro", "press_start", "welcome", "menu_quickmatch", "menu_lobbies",
  "menu_garage", "menu_practice", "menu_rankings", "menu_options",
] as const;

export type AnnouncerLineId = (typeof ANNOUNCER_LINES)[number];

/** Priority bands. MENU lines must NEVER play over match audio. */
export const PRIORITY = { MENU: 0, LOW: 1, MID: 2, HIGH: 3 } as const;
export type AnnouncerPriority = (typeof PRIORITY)[keyof typeof PRIORITY];

const HIGH_LINES: readonly AnnouncerLineId[] = [
  "countdown_3", "countdown_2", "countdown_1", "go", "victory", "winner",
];
// Power-up / weapon pickups are MID (same band as kills and round-clock calls),
// NOT low: a low pickup line would be INTERRUPTED by a same-frame time_10, so
// grabbing a power-up near the 10 s mark played "ten seconds" over the pickup.
// As MID they queue with round events instead of being cut off, and — emitted
// before time thresholds in the tracker — they still play first.
const LOW_LINES: readonly AnnouncerLineId[] = [];
const MENU_LINES: readonly AnnouncerLineId[] = [
  "title_intro", "press_start", "welcome", "menu_quickmatch", "menu_lobbies",
  "menu_garage", "menu_practice", "menu_rankings", "menu_options",
];

export function priorityOf(line: AnnouncerLineId): AnnouncerPriority {
  if (HIGH_LINES.includes(line)) return PRIORITY.HIGH;
  if (LOW_LINES.includes(line)) return PRIORITY.LOW;
  if (MENU_LINES.includes(line)) return PRIORITY.MENU;
  return PRIORITY.MID;
}

/** Per-line repeat cooldowns (ms). Anything not listed uses the default. */
const COOLDOWN_MS: Partial<Record<AnnouncerLineId, number>> = {
  kill: 2000,
  death: 2000,
  lead_taken: 10_000,
  rocket_warning: 4000,
  title_intro: 1500, press_start: 1500, welcome: 1500,
  menu_quickmatch: 1500, menu_lobbies: 1500, menu_garage: 1500,
  menu_practice: 1500, menu_rankings: 1500, menu_options: 1500,
};
const DEFAULT_COOLDOWN_MS = 800;

export function cooldownOf(line: AnnouncerLineId): number {
  return COOLDOWN_MS[line] ?? DEFAULT_COOLDOWN_MS;
}

/** Minimum gap between the START of one clip and the start of the next. */
export const GLOBAL_MIN_GAP_MS = 250;
/** A queued line older than this is stale (a late "3" is worse than silence). */
const QUEUE_TTL_MS = 3000;
const QUEUE_MAX = 2;

export type ScheduleAction = "start" | "interrupt" | "queue" | "drop";

interface QueuedLine {
  line: AnnouncerLineId;
  priority: AnnouncerPriority;
  at: number;
}

/**
 * Pure playback arbiter. Callers `request()` lines; the owner starts the clip
 * on "start"/"interrupt", and reports `clipEnded`/`startFailed` to advance the
 * queue. Everything takes an explicit `now` so tests can drive time.
 */
export class AnnouncerScheduler {
  private playing: { line: AnnouncerLineId; priority: AnnouncerPriority } | null = null;
  private queue: QueuedLine[] = [];
  private lastStartAt = Number.NEGATIVE_INFINITY;
  private lastLineAt = new Map<AnnouncerLineId, number>();

  playingLine(): AnnouncerLineId | null {
    return this.playing?.line ?? null;
  }

  hasQueued(): boolean {
    return this.queue.length > 0;
  }

  request(line: AnnouncerLineId, now: number): ScheduleAction {
    const pr = priorityOf(line);
    const last = this.lastLineAt.get(line);
    if (last !== undefined && now - last < cooldownOf(line)) return "drop";

    if (this.playing) {
      if (pr > this.playing.priority) {
        this.markStart(line, pr, now);
        return "interrupt";
      }
      // Menu chatter must never queue behind (or play over) match audio —
      // it may queue behind another menu line (title_intro → welcome).
      if (pr === PRIORITY.MENU && this.playing.priority > PRIORITY.MENU) return "drop";
      return this.enqueue(line, pr, now);
    }

    if (now - this.lastStartAt < GLOBAL_MIN_GAP_MS) {
      return this.enqueue(line, pr, now);
    }
    this.markStart(line, pr, now);
    return "start";
  }

  /** Current clip finished cleanly. Returns the next line to start, if any. */
  clipEnded(now: number): AnnouncerLineId | null {
    this.playing = null;
    return this.poll(now);
  }

  /**
   * The clip never actually played (autoplay lock, missing file). Frees the
   * slot AND the line's cooldown stamp so a later retry is not shadow-blocked
   * by a play that made no sound.
   */
  startFailed(line: AnnouncerLineId, now: number): AnnouncerLineId | null {
    if (this.playing?.line === line) this.playing = null;
    if (this.lastLineAt.get(line) !== undefined) this.lastLineAt.delete(line);
    return this.poll(now);
  }

  /** Start the best queued line when idle and past the global gap. */
  poll(now: number): AnnouncerLineId | null {
    if (this.playing) return null;
    this.queue = this.queue.filter((q) => now - q.at <= QUEUE_TTL_MS);
    if (this.queue.length === 0) return null;
    if (now - this.lastStartAt < GLOBAL_MIN_GAP_MS) return null;
    const next = this.queue.shift()!;
    this.markStart(next.line, next.priority, now);
    return next.line;
  }

  /** Drop everything (announcer disabled mid-flight). */
  clear(): void {
    this.playing = null;
    this.queue = [];
  }

  private enqueue(line: AnnouncerLineId, pr: AnnouncerPriority, now: number): ScheduleAction {
    if (this.queue.some((q) => q.line === line)) return "drop";
    this.queue.push({ line, priority: pr, at: now });
    // Highest priority first; FIFO within a band.
    this.queue.sort((a, b) => b.priority - a.priority || a.at - b.at);
    if (this.queue.length > QUEUE_MAX) this.queue.length = QUEUE_MAX;
    return "queue";
  }

  private markStart(line: AnnouncerLineId, pr: AnnouncerPriority, now: number): void {
    this.playing = { line, priority: pr };
    this.lastStartAt = now;
    this.lastLineAt.set(line, now);
  }
}

// ── Settings ─────────────────────────────────────────────────────────────────

export interface AnnouncerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const ANNOUNCER_MUTED_KEY = "neon-arena.announcer-muted";
export const ANNOUNCER_VOLUME_KEY = "neon-arena.announcer-volume";
export const ANNOUNCER_PACK_KEY = "neon-arena.announcer-pack";

export const ANNOUNCER_DEFAULT_VOLUME = 0.7;
export const ANNOUNCER_DEFAULT_PACK = "adam";

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function defaultStorage(): AnnouncerStorage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null; // storage access can throw (privacy modes)
  }
}

export class AnnouncerSettings {
  private _enabled = true;
  private _volume = ANNOUNCER_DEFAULT_VOLUME;
  private _pack = ANNOUNCER_DEFAULT_PACK;

  constructor(private readonly storage: AnnouncerStorage | null = defaultStorage()) {
    if (this.storage) {
      try {
        const muted = this.storage.getItem(ANNOUNCER_MUTED_KEY);
        if (muted !== null) this._enabled = muted !== "true";
        const vol = this.storage.getItem(ANNOUNCER_VOLUME_KEY);
        if (vol !== null) {
          const f = parseFloat(vol);
          if (Number.isFinite(f)) this._volume = clamp01(f);
        }
        const pack = this.storage.getItem(ANNOUNCER_PACK_KEY);
        if (pack) this._pack = pack;
      } catch {
        /* unreadable storage — keep defaults */
      }
    }
  }

  get enabled(): boolean { return this._enabled; }
  get volume(): number { return this._volume; }
  get pack(): string { return this._pack; }

  setEnabled(on: boolean): void {
    this._enabled = on;
    this.persist(ANNOUNCER_MUTED_KEY, String(!on));
  }

  setVolume(v: number): void {
    this._volume = clamp01(v);
    this.persist(ANNOUNCER_VOLUME_KEY, String(this._volume));
  }

  setPack(id: string): void {
    if (!id) return;
    this._pack = id;
    this.persist(ANNOUNCER_PACK_KEY, id);
  }

  private persist(key: string, value: string): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(key, value);
    } catch {
      /* quota / privacy mode — non-fatal */
    }
  }
}

// ── Manifest ─────────────────────────────────────────────────────────────────

export interface AnnouncerPack {
  id: string;
  label: string;
}

export interface AnnouncerManifest {
  generatedAt?: string;
  lines: string[];
  packs: AnnouncerPack[];
}

/** Known packs — used until (or in place of) the manifest, so the OPTIONS
 * voice picker works even when the asset generation hasn't landed yet. */
export const FALLBACK_PACKS: readonly AnnouncerPack[] = [
  { id: "adam", label: "ADAM" },
  { id: "harry", label: "HARRY" },
  { id: "callum", label: "CALLUM" },
  { id: "charlie", label: "CHARLIE" },
  { id: "lily", label: "LILY" },
];

function defaultBaseUrl(): string {
  try {
    const env = (import.meta as unknown as { env?: { BASE_URL?: string } }).env;
    const base = env?.BASE_URL ?? "/";
    return `${base.endsWith("/") ? base : `${base}/`}audio/announcer/`;
  } catch {
    return "/audio/announcer/";
  }
}

/** Clip watchdog — no announcer line is anywhere near this long. */
const CLIP_WATCHDOG_MS = 8000;
const POLL_MS = 300;

/**
 * Browser-facing announcer. Inert (but safe to call) in environments without
 * HTMLAudioElement — every audio/network failure is swallowed by design.
 */
export class Announcer {
  private readonly scheduler = new AnnouncerScheduler();
  private manifest: AnnouncerManifest | null = null;
  private manifestPromise: Promise<void> | null = null;
  private clips = new Map<string, HTMLAudioElement>();
  private preloadedPack: string | null = null;
  private current: HTMLAudioElement | null = null;
  private currentLine: AnnouncerLineId | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private _hasPlayed = false;

  constructor(
    private readonly settings: AnnouncerSettings = new AnnouncerSettings(),
    private readonly baseUrl: string = defaultBaseUrl(),
  ) {}

  get enabled(): boolean { return this.settings.enabled; }
  get volume(): number { return this.settings.volume; }
  get pack(): string { return this.settings.pack; }
  /** True once at least one clip has actually started (autoplay unlocked). */
  get hasPlayed(): boolean { return this._hasPlayed; }

  setEnabled(on: boolean): void {
    this.settings.setEnabled(on);
    if (!on) this.stopAll();
  }

  setVolume(v: number): void {
    this.settings.setVolume(v);
    if (this.current) {
      try { this.current.volume = this.settings.volume; } catch { /* ignore */ }
    }
  }

  setPack(id: string): void {
    this.settings.setPack(id);
    this.preloadPack(id);
  }

  /** Manifest packs when loaded, known-pack fallback otherwise. */
  packs(): readonly AnnouncerPack[] {
    return this.manifest?.packs?.length ? this.manifest.packs : FALLBACK_PACKS;
  }

  /** Lazy manifest fetch; safe to call repeatedly, never rejects. */
  loadManifest(): Promise<void> {
    if (this.manifestPromise) return this.manifestPromise;
    this.manifestPromise = (async () => {
      try {
        if (typeof fetch !== "function") return;
        const res = await fetch(`${this.baseUrl}manifest.json`);
        if (!res.ok) return;
        const body = (await res.json()) as AnnouncerManifest;
        if (Array.isArray(body?.packs)) this.manifest = body;
      } catch {
        /* missing manifest never breaks anything */
      }
    })();
    return this.manifestPromise;
  }

  /** Warm from a user gesture: kick the manifest + selected pack's clips. */
  unlock(): void {
    void this.loadManifest();
    this.preloadPack(this.settings.pack);
  }

  /**
   * Ask to speak `lineId`. One clip at a time; higher priority interrupts,
   * equal/lower queues (or drops on cooldown); menu lines never play over
   * match audio. All failures silent.
   */
  announce(lineId: AnnouncerLineId): void {
    try {
      if (!this.settings.enabled) return;
      if (!hasAudioElement()) return;
      void this.loadManifest();
      this.preloadPack(this.settings.pack);
      const now = Date.now();
      const action = this.scheduler.request(lineId, now);
      if (action === "drop") return;
      if (action === "queue") { this.armPoll(); return; }
      if (action === "interrupt") this.stopCurrentElement();
      this.startClip(lineId);
    } catch {
      /* an announcer failure must never break the game */
    }
  }

  /** Voice-picker preview: play one clip of the CURRENT pack, bypassing the
   * scheduler entirely (never jams match state). */
  preview(lineId: AnnouncerLineId = "kill"): void {
    try {
      if (!this.settings.enabled || !hasAudioElement()) return;
      const el = new Audio(this.urlFor(this.settings.pack, lineId));
      el.volume = this.settings.volume;
      void el.play().catch(() => { /* locked or missing — silent */ });
    } catch {
      /* ignore */
    }
  }

  /** Stop the current clip and forget everything queued. */
  stopAll(): void {
    this.stopCurrentElement();
    this.scheduler.clear();
    if (this.pollTimer !== null) { clearTimeout(this.pollTimer); this.pollTimer = null; }
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private startClip(line: AnnouncerLineId): void {
    const el = this.clipFor(this.settings.pack, line);
    if (!el) {
      // No element could even be built — release the slot straight on the
      // scheduler (finishClip's stale-guard only covers a *started* clip).
      const next = this.scheduler.startFailed(line, Date.now());
      if (next) this.startClip(next);
      return;
    }
    this.current = el;
    this.currentLine = line;
    try { el.volume = this.settings.volume; } catch { /* ignore */ }
    try { el.currentTime = 0; } catch { /* not seekable yet — plays from 0 anyway */ }
    el.onended = () => this.finishClip(line, false);
    el.onerror = () => this.finishClip(line, true);
    let p: Promise<void> | undefined;
    try {
      p = el.play();
    } catch {
      this.finishClip(line, true);
      return;
    }
    if (p && typeof p.then === "function") {
      p.then(() => { this._hasPlayed = true; })
        .catch(() => this.finishClip(line, true));
    }
    this.armWatchdog(line);
  }

  private finishClip(line: AnnouncerLineId, failed: boolean): void {
    if (this.currentLine !== line) return; // stale callback after an interrupt
    this.clearWatchdog();
    this.current = null;
    this.currentLine = null;
    const now = Date.now();
    const next = failed ? this.scheduler.startFailed(line, now) : this.scheduler.clipEnded(now);
    if (next) this.startClip(next);
    else if (this.scheduler.hasQueued()) this.armPoll();
  }

  private stopCurrentElement(): void {
    this.clearWatchdog();
    const el = this.current;
    this.current = null;
    this.currentLine = null;
    if (el) {
      el.onended = null;
      el.onerror = null;
      try { el.pause(); } catch { /* ignore */ }
    }
  }

  private armPoll(): void {
    if (this.pollTimer !== null) return;
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      try {
        const next = this.scheduler.poll(Date.now());
        if (next) this.startClip(next);
        else if (this.scheduler.hasQueued()) this.armPoll();
      } catch { /* ignore */ }
    }, POLL_MS);
  }

  private armWatchdog(line: AnnouncerLineId): void {
    this.clearWatchdog();
    this.watchdog = setTimeout(() => {
      this.watchdog = null;
      this.finishClip(line, false); // treat as ended — never jam the queue
    }, CLIP_WATCHDOG_MS);
  }

  private clearWatchdog(): void {
    if (this.watchdog !== null) { clearTimeout(this.watchdog); this.watchdog = null; }
  }

  private urlFor(pack: string, line: AnnouncerLineId): string {
    return `${this.baseUrl}${encodeURIComponent(pack)}/${line}.mp3`;
  }

  private clipFor(pack: string, line: AnnouncerLineId): HTMLAudioElement | null {
    if (!hasAudioElement()) return null;
    const key = `${pack}/${line}`;
    let el = this.clips.get(key) ?? null;
    if (!el) {
      try {
        el = new Audio(this.urlFor(pack, line));
        el.preload = "auto";
        this.clips.set(key, el);
      } catch {
        return null;
      }
    }
    return el;
  }

  /** Build (and start fetching) the selected pack's clip pool, once per pack. */
  private preloadPack(pack: string): void {
    if (this.preloadedPack === pack || !hasAudioElement()) return;
    this.preloadedPack = pack;
    try {
      for (const line of ANNOUNCER_LINES) this.clipFor(pack, line);
    } catch {
      /* ignore */
    }
  }
}

function hasAudioElement(): boolean {
  return typeof Audio !== "undefined";
}

/** App-wide singleton (browser). Tests construct their own pieces directly. */
export const announcer = new Announcer();
